import { OfferType } from "@KassaroAB/shared/types/enums/OfferType";
import type { OfferTarget } from "@KassaroAB/shared/types/offer";
import type { WeeklyOpeningHours } from "@KassaroAB/shared/types/openingHours";

export const emptyOfferTarget = (): OfferTarget => ({
  productIds: [],
  groupIds: [],
});

export const WEEK_DAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

export const createDefaultWeeklyHours = (): WeeklyOpeningHours => ({
  monday: { closed: true },
  tuesday: { closed: true },
  wednesday: { closed: true },
  thursday: { closed: true },
  friday: { closed: true },
  saturday: { closed: true },
  sunday: { closed: true },
});

export const OFFER_TYPES = [
  OfferType.PERCENTAGE,
  OfferType.BUY_X_PAY_Y,
  OfferType.QUANTITY_PRICE,
  OfferType.FIXED_PRICE,
  OfferType.COMBO,
] as const;

export const offerTypeLabel = (type: OfferType, t: (key: string) => string) =>
  t(`offerForm.types.${type}`);

export type OfferCommonDefaultValues = {
  name: string;
  receiptText: string;
  type: OfferType;
  enabled: boolean;
  priority: number;
  useSchedule: boolean;
  startsAt: string;
  endsAt: string;
  useHours: boolean;
  weekly: WeeklyOpeningHours;
};

export const getOfferCommonDefaults = (offer?: {
  name: string;
  receiptText?: string | null;
  type: OfferType;
  enabled: boolean;
  priority: number;
  schedule?: {
    startsAt: string | null;
    endsAt: string | null;
    hours: { weekly: WeeklyOpeningHours; overrides?: unknown } | null;
  } | null;
} | null): OfferCommonDefaultValues => ({
  name: offer?.name ?? "",
  receiptText: offer?.receiptText ?? "",
  type: offer?.type ?? OfferType.PERCENTAGE,
  enabled: offer?.enabled ?? true,
  priority: offer?.priority ?? 0,
  useSchedule: offer?.schedule != null,
  startsAt: offer?.schedule?.startsAt ?? "",
  endsAt: offer?.schedule?.endsAt ?? "",
  useHours: offer?.schedule?.hours != null,
  weekly: offer?.schedule?.hours?.weekly ?? createDefaultWeeklyHours(),
});

export const buildOfferSchedule = (value: {
  useSchedule: boolean;
  startsAt: string;
  endsAt: string;
  useHours: boolean;
  weekly: WeeklyOpeningHours;
}) =>
  value.useSchedule
    ? {
        startsAt: value.startsAt.trim() === "" ? null : value.startsAt,
        endsAt: value.endsAt.trim() === "" ? null : value.endsAt,
        hours: value.useHours
          ? { weekly: value.weekly, overrides: [] }
          : null,
      }
    : null;

export const buildOfferBase = (value: {
  name: string;
  receiptText: string;
  enabled: boolean;
  priority: number;
  useSchedule: boolean;
  startsAt: string;
  endsAt: string;
  useHours: boolean;
  weekly: WeeklyOpeningHours;
}) => ({
  name: value.name.trim(),
  receiptText:
    value.receiptText.trim() === "" ? null : value.receiptText.trim(),
  enabled: value.enabled,
  priority: Number(value.priority),
  schedule: buildOfferSchedule(value),
});
